import React, { useState, createContext, useContext } from 'react';
export interface Mark {
  id: string;
  x: number;
  y: number;
  description: string;
  date: string;
}
export interface WeightEntry {
  id: string;
  weight: number;
  date: string;
}
export interface Horse {
  id: string;
  name: string;
  birthYear: number;
  breed?: string;
  lineage?: string;
  marks: Mark[];
  weights: WeightEntry[];
}
interface HorseContextType {
  horses: Horse[];
  addHorse: (horse: Omit<Horse, 'id' | 'marks' | 'weights'>) => void;
  updateHorse: (id: string, horse: Partial<Horse>) => void;
  deleteHorse: (id: string) => void;
  addWeight: (horseId: string, weight: number) => void;
  addMark: (horseId: string, mark: Omit<Mark, 'id' | 'date'>) => void;
}
const HorseContext = createContext<HorseContextType | undefined>(undefined);
export const HorseProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [horses, setHorses] = useState<Horse[]>([{
    id: '1',
    name: 'Bella',
    birthYear: 2015,
    breed: 'Hannoveraner',
    lineage: 'Donnerhall',
    marks: [{
      id: 'mark1',
      x: 65,
      y: 30,
      description: 'Weißer Fleck an der Stirn',
      date: '12.05.2023'
    }, {
      id: 'mark2',
      x: 80,
      y: 55,
      description: 'Narbe nach Schnittverletzung',
      date: '23.07.2023'
    }, {
      id: 'mark3',
      x: 35,
      y: 60,
      description: 'Altes Sehnenproblem, verhärtet',
      date: '15.01.2024'
    }],
    weights: [{
      id: '1',
      weight: 480,
      date: '2023-01-15'
    }, {
      id: '2',
      weight: 485,
      date: '2023-02-20'
    }, {
      id: '3',
      weight: 482,
      date: '2023-03-18'
    }, {
      id: '4',
      weight: 479,
      date: '2023-04-22'
    }, {
      id: '5',
      weight: 484,
      date: '2023-05-15'
    }, {
      id: '6',
      weight: 488,
      date: '2023-06-10'
    }, {
      id: '7',
      weight: 492,
      date: '2023-07-05'
    }, {
      id: '8',
      weight: 490,
      date: '2023-08-18'
    }, {
      id: '9',
      weight: 486,
      date: '2023-09-20'
    }, {
      id: '10',
      weight: 483,
      date: '2023-10-15'
    }, {
      id: '11',
      weight: 485,
      date: '2023-11-20'
    }, {
      id: '12',
      weight: 481,
      date: '2023-12-12'
    }, {
      id: '13',
      weight: 478,
      date: '2024-01-18'
    }, {
      id: '14',
      weight: 482,
      date: '2024-02-22'
    }, {
      id: '15',
      weight: 487,
      date: '2024-03-15'
    }]
  }, {
    id: '2',
    name: 'Max',
    birthYear: 2018,
    breed: 'Holsteiner',
    lineage: 'Contendro',
    marks: [{
      id: 'mark4',
      x: 45,
      y: 25,
      description: 'Kleine Wunde am Ohr',
      date: '02.02.2024'
    }, {
      id: 'mark5',
      x: 55,
      y: 75,
      description: 'Hufverletzung vorne rechts',
      date: '17.11.2023'
    }],
    weights: [{
      id: '1',
      weight: 460,
      date: '2023-01-05'
    }, {
      id: '2',
      weight: 463,
      date: '2023-02-12'
    }, {
      id: '3',
      weight: 458,
      date: '2023-03-25'
    }, {
      id: '4',
      weight: 464,
      date: '2023-04-18'
    }, {
      id: '5',
      weight: 467,
      date: '2023-05-22'
    }, {
      id: '6',
      weight: 470,
      date: '2023-06-15'
    }, {
      id: '7',
      weight: 472,
      date: '2023-07-20'
    }, {
      id: '8',
      weight: 468,
      date: '2023-08-10'
    }, {
      id: '9',
      weight: 465,
      date: '2023-09-05'
    }, {
      id: '10',
      weight: 462,
      date: '2023-10-18'
    }, {
      id: '11',
      weight: 459,
      date: '2023-11-22'
    }, {
      id: '12',
      weight: 464,
      date: '2023-12-15'
    }]
  }, {
    id: '3',
    name: 'Luna',
    birthYear: 2016,
    breed: 'Trakehner',
    lineage: 'Gribaldi',
    marks: [{
      id: 'mark6',
      x: 70,
      y: 45,
      description: 'Charakteristischer schwarzer Fleck',
      date: '10.06.2023'
    }, {
      id: 'mark7',
      x: 40,
      y: 40,
      description: 'Alte Operationsnarbe',
      date: '05.09.2022'
    }, {
      id: 'mark8',
      x: 60,
      y: 85,
      description: 'Empfindliche Stelle am Hinterbein',
      date: '21.03.2024'
    }, {
      id: 'mark9',
      x: 25,
      y: 50,
      description: 'Leichte Schwellung nach Insektenstich',
      date: '14.04.2024'
    }],
    weights: [{
      id: '1',
      weight: 510,
      date: '2023-02-10'
    }, {
      id: '2',
      weight: 508,
      date: '2023-03-15'
    }, {
      id: '3',
      weight: 512,
      date: '2023-04-20'
    }, {
      id: '4',
      weight: 515,
      date: '2023-05-25'
    }, {
      id: '5',
      weight: 518,
      date: '2023-06-30'
    }, {
      id: '6',
      weight: 520,
      date: '2023-07-15'
    }, {
      id: '7',
      weight: 516,
      date: '2023-08-20'
    }, {
      id: '8',
      weight: 514,
      date: '2023-09-25'
    }, {
      id: '9',
      weight: 512,
      date: '2023-10-30'
    }, {
      id: '10',
      weight: 510,
      date: '2023-11-15'
    }, {
      id: '11',
      weight: 508,
      date: '2023-12-20'
    }, {
      id: '12',
      weight: 512,
      date: '2024-01-25'
    }, {
      id: '13',
      weight: 515,
      date: '2024-02-28'
    }]
  }]);
  const addHorse = (horse: Omit<Horse, 'id' | 'marks' | 'weights'>) => {
    const newHorse: Horse = {
      ...horse,
      id: Date.now().toString(),
      marks: [],
      weights: []
    };
    setHorses(prev => [...prev, newHorse]);
  };
  const updateHorse = (id: string, horse: Partial<Horse>) => {
    setHorses(prev => prev.map(h => h.id === id ? {
      ...h,
      ...horse
    } : h));
  };
  const deleteHorse = (id: string) => {
    setHorses(prev => prev.filter(h => h.id !== id));
  };
  const addWeight = (horseId: string, weight: number) => {
    const weightEntry: WeightEntry = {
      id: Date.now().toString(),
      weight,
      date: new Date().toISOString()
    };
    setHorses(prev => prev.map(h => h.id === horseId ? {
      ...h,
      weights: [...h.weights, weightEntry]
    } : h));
  };
  const addMark = (horseId: string, mark: Omit<Mark, 'id' | 'date'>) => {
    const markEntry: Mark = {
      ...mark,
      id: Date.now().toString(),
      date: new Date().toLocaleDateString('de-DE')
    };
    setHorses(prev => prev.map(h => {
      if (h.id === horseId) {
        return {
          ...h,
          marks: [...h.marks, markEntry]
        };
      }
      return h;
    }));
    console.log(`Added mark to horse ${horseId}:`, markEntry);
  };
  return <HorseContext.Provider value={{
    horses,
    addHorse,
    updateHorse,
    deleteHorse,
    addWeight,
    addMark
  }} data-id="element-383">
      {children}
    </HorseContext.Provider>;
};
export const useHorse = () => {
  const context = useContext(HorseContext);
  if (context === undefined) {
    throw new Error('useHorse must be used within a HorseProvider');
  }
  return context;
};